/**
 * Funnel Fenix Chatbot Block
 *
 * Injection block that loads the Funnel Fenix chatbot (LeaseHawk ACE chat).
 * The vendor snippet stored on the property looks like:
 *
 *   <script type="text/javascript" id="ace-chat-script" data-site-id="32459"
 *           src="https://ace-chat.leasehawk.com/dist/js/ace.js"></script>
 *
 * The only per-property value is `data-site-id`; the element id and src are
 * fixed. The token accepts the full snippet or the bare site id, and the block
 * rebuilds the canonical tag so whatever ops pasted yields the same script.
 *
 * Why singleton + never-tear-down: ACE paints its launcher directly into
 * <body> with no teardown API, so re-injecting duplicates the widget. The
 * vendor's fixed element id doubles as the singleton marker, so repeat mounts
 * (StrictMode, SPA navigation) and a second block instance are no-ops. This
 * mirrors the entrata / anyonehome / betterbot chatbot blocks.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const SCRIPT_ID = 'ace-chat-script';
const SCRIPT_SRC = 'https://ace-chat.leasehawk.com/dist/js/ace.js';

const SITE_ID_ATTR_PATTERN = /data-site-id\s*=\s*["']?\s*(\d+)/i;
const SITE_ID_PATTERN = /^\d+$/;

/**
 * Accept the vendor snippet or a bare numeric site id. Anything else (markup
 * or a URL with no `data-site-id`, non-numeric text) is unusable → null.
 */
function extractSiteId(value: string | null | undefined) {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const match = trimmed.match(SITE_ID_ATTR_PATTERN);
  if (match) return match[1];
  return SITE_ID_PATTERN.test(trimmed) ? trimmed : null;
}

function FunnelFenixChatbotComponent(_props: BlockProps) {
  const siteId = extractSiteId(
    useBlockToken('site_id', { defaultField: 'custom_fenix_chatbot' }),
  );

  useEffect(() => {
    if (!siteId || document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = SCRIPT_ID;
    script.setAttribute('data-site-id', siteId);
    script.src = SCRIPT_SRC;
    document.head.appendChild(script);
  }, [siteId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'FunnelFenixChatbot',
  displayName: 'Funnel Fenix Chatbot',
  tokens: {
    site_id: {
      label: 'Fenix Embed Script or Site ID',
      type: 'text',
      defaultField: 'custom_fenix_chatbot',
      suggestedFields: ['custom_fenix_chatbot'],
      description:
        'Funnel Fenix (LeaseHawk ACE chat) embed script as stored on the property, or the bare site id (e.g. "32459"). Full snippets are parsed for the data-site-id value and the canonical tag is rebuilt.',
      required: true,
    },
  },
};

export const funnelFenixChatbotBlock: BlockDefinition = {
  id: 'funnel-fenix-chatbot',
  name: 'Funnel Fenix Chatbot',
  description:
    'Funnel Fenix (LeaseHawk ACE) chatbot injected from the property embed script or site id. The floating launcher is the UI — clicking it opens the chat flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: FunnelFenixChatbotComponent,
  contentSchema,
  tags: ['funnel', 'fenix', 'leasehawk', 'ace', 'chatbot', 'chat', 'leasing', 'widget'],
};
